import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { type AppState, type PyramidItem as LocalPyramidItem, type Category, type TimeHorizon, type StrategicDirection } from './types';
import { ItemInput } from './components/ItemInput';
import { PyramidView } from './components/PyramidView';
import { Upload, Download, FileJson, FileSpreadsheet, RotateCcw, CheckCircle, Database, Loader2, AlertCircle, Plus, ExternalLink, X } from 'lucide-react';
import { getIntegrationsApi, type MiroBoard } from '../../lib/integrationsApi';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { PyramidImportModal } from '../../components/prioritization/pyramid/PyramidImportModal';
import { usePyramidStore, type PyramidItem as StorePyramidItem } from '../../stores/prioritization/pyramidStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCreateRights } from '../../lib/authorization/createRights';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { exportPyramidToExcel, exportPyramidToJSON } from '../../lib/pyramidImportService';
import { isSupabaseAvailable, generateUUID } from '../../lib/supabaseSync';
import { useToolHostContext } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { StackRankingLive } from '../../components/prioritization/session/liveExperience/liveTools/StackRankingLive';

// Decode HTML entities in strings
function decodeHtmlEntities(text: string): string {
  if (!text) return text;
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x22;/g, '"');
}

// Convert store item to local PyramidItem format for PyramidView
function toLocalItem(item: StorePyramidItem): LocalPyramidItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    category: decodeHtmlEntities(item.category) as Category,
    timeHorizon: item.timeHorizon as TimeHorizon,
    strategicDirection: 'Get House in Order' as StrategicDirection, // Derived
    rank: item.rank,
  };
}

function MaxPrioritiesPyramidContent({ onImportItems, onGetResults }: { 
  onImportItems?: (handler: (items: ToolItem[]) => void) => void;
  onGetResults?: (handler: () => ToolResultItem[]) => void;
}) {
  const toolMode = useToolMode();
  const hostContext = useToolHostContext();
  const { isLiveSession } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  const importSourceLabel = hostContext === 'session' ? 'session' : 'workspace';
  const [liveRankItems, setLiveRankItems] = useState<{ id: string; name: string; description?: string }[]>([]);
  const [liveRanking, setLiveRanking] = useState<string[]>([]);
  
  // Supabase store
  const {
    pyramids,
    items: storeItems,
    activePyramidId,
    syncState,
    fetchFromSupabase,
    createPyramid,
    updatePyramid,
    deletePyramid,
    setActivePyramid,
    addItem,
    updateItem,
    deleteItem,
    updateItemRank,
    updateCategories,
    importItems,
    getItemsForPyramid,
  } = usePyramidStore();
  
  // Log store state for debugging
  console.log('[MaxPrioritiesPyramid] Store state:', {
    pyramidsCount: pyramids.length,
    itemsCount: storeItems.length,
    activePyramidId,
  });
  
  const userId = useUserStore(state => state.userId);
  const userName = useUserStore(state => state.userName);
  // Import from Miro or Excel creates an item per pyramid entry: the items
  // insert rule (migration 751) takes items.create, so both Import controls
  // are drawn only for holders (build contract §2.8).
  const mayImport = useCreateRights().items;
  
  // Candidates store for creating candidates linked to pyramid items
  const { createItem } = useItemsStore();
  
  // Local UI state
  const [state, setState] = useState<AppState>('setup');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showMiroExportModal, setShowMiroExportModal] = useState(false);
  const [showPyramidSelector, setShowPyramidSelector] = useState(false);
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});
  
  // Miro export state
  const [miroBoards, setMiroBoards] = useState<MiroBoard[]>([]);
  const [selectedMiroBoard, setSelectedMiroBoard] = useState<string>('');
  const [newMiroBoardName, setNewMiroBoardName] = useState('');
  const [miroExportLoading, setMiroExportLoading] = useState(false);
  const [miroExportError, setMiroExportError] = useState<string | null>(null);
  const [miroExportSuccess, setMiroExportSuccess] = useState<{ 
    boardUrl?: string; 
    itemsCreated: number;
    itemsUpdated?: number;
    itemsSkipped?: number;
    duplicatesFound?: number;
  } | null>(null);
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update' | 'create'>('skip');
  
  // Get active pyramid - derive from already-subscribed values
  const activePyramid = useMemo(() => {
    if (!activePyramidId) return undefined;
    return pyramids.find(p => p.id === activePyramidId);
  }, [activePyramidId, pyramids]);
  
  // Get pyramid items - derive from subscribed items array (storeItems)
  const pyramidItems = useMemo(() => {
    if (!activePyramidId) return [];
    return storeItems
      .filter(i => i.pyramidId === activePyramidId)
      .sort((a, b) => a.rank - b.rank);
  }, [activePyramidId, storeItems]);
  
  // Debug logging
  console.log('[MaxPrioritiesPyramid] Render state:', { 
    state, 
    activePyramidId, 
    activePyramid: activePyramid ? { id: activePyramid.id, name: activePyramid.name } : null,
    pyramidsCount: pyramids.length,
    pyramidIds: pyramids.map(p => p.id),
    pyramidItemsCount: pyramidItems.length,
    showToolbar: (state === 'building' || state === 'viewing') && !!activePyramid
  });
  
  // Convert to local format for PyramidView
  const localItems: LocalPyramidItem[] = pyramidItems.map(toLocalItem);
  // Decode any HTML entities in category names
  const categories: Category[] = (activePyramid?.categories || ['Process Automation', 'Tech Debt', 'Defects', 'Other Priorities'])
    .map(cat => decodeHtmlEntities(cat) as Category);
  
  // Load the person's own pyramids on mount — not in a session, which ranks
  // the session's items and shows none of them.
  useEffect(() => {
    if (isLiveSession) return;
    if (isSupabaseAvailable()) {
      fetchFromSupabase().then(() => {
        // If there are pyramids, go to building state
        const store = usePyramidStore.getState();
        if (store.pyramids.length > 0) {
          // Set the most recently updated pyramid as active
          const sorted = [...store.pyramids].sort((a, b) => 
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
          setActivePyramid(sorted[0].id);
          setState('building');
        }
      });
    }
  }, [isLiveSession, fetchFromSupabase, setActivePyramid]);
  
  // Recovery effect: if we have items but no pyramid, try to recover
  useEffect(() => {
    if ((state === 'building' || state === 'viewing') && !activePyramid && activePyramidId) {
      console.warn('[MaxPrioritiesPyramid] State mismatch: building/viewing but no activePyramid. Attempting recovery...');
      console.log('[MaxPrioritiesPyramid] activePyramidId:', activePyramidId);
      console.log('[MaxPrioritiesPyramid] pyramids:', pyramids.map(p => p.id));
      
      // If we have items for this pyramid ID, the pyramid should exist
      const itemsForThisPyramid = getItemsForPyramid(activePyramidId);
      if (itemsForThisPyramid.length > 0) {
        console.log('[MaxPrioritiesPyramid] Found', itemsForThisPyramid.length, 'items for this pyramid ID, refetching...');
        fetchFromSupabase().then(() => {
          const store = usePyramidStore.getState();
          if (store.pyramids.find(p => p.id === activePyramidId)) {
            console.log('[MaxPrioritiesPyramid] Pyramid found after refetch!');
          } else {
            console.error('[MaxPrioritiesPyramid] Pyramid still not found after refetch. Creating orphan recovery pyramid...');
            // Create a recovery pyramid entry
            createPyramid({
              name: 'Recovered Pyramid',
              createdBy: userId,
            }).then(newId => {
              // We can't easily migrate items to the new pyramid, but at least we can try
              console.log('[MaxPrioritiesPyramid] Created recovery pyramid:', newId);
            });
          }
        });
      }
    }
  }, [state, activePyramid, activePyramidId, pyramids, getItemsForPyramid, fetchFromSupabase, createPyramid, userId]);
  
  // Handler for importing items from workspace (app mode).
  // Auto-creates a pyramid if none is active yet (zero-state import path).
  const handleImportItems = useCallback(async (toolItems: ToolItem[]) => {
    // In a session this is a ranking of the session's items and nothing else:
    // no standalone pyramid is created, so none of its Export, Import, name or
    // sync controls appear, and no "My Pyramid" is left behind per participant.
    if (isLiveSession) {
      const ranked = toolItems.map(item => ({
        id: item.sourceId ?? item.id,
        name: item.name,
        description: item.description,
      }));
      setLiveRankItems(ranked);
      setLiveRanking(ranked.map(i => i.id));
      setSourceIdMap(prev => ({
        ...prev,
        ...Object.fromEntries(toolItems.map(item => [item.id, item.sourceId ?? item.id])),
      }));
      return;
    }

    let pyramidId = activePyramidId;
    if (!pyramidId) {
      pyramidId = await createPyramid({ name: 'My Pyramid', createdBy: userId });
      setActivePyramid(pyramidId);
      setState('building');
    }

    const itemsToImport = toolItems.map(item => ({
      name: item.name,
      description: item.description,
      timeHorizon: 'Run' as TimeHorizon,
      category: 'Other Priorities',
      source: 'imported' as const,
    }));

    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));


    importItems(pyramidId, itemsToImport, userId);
  }, [activePyramidId, userId, createPyramid, setActivePyramid, importItems, isLiveSession]);

  // Handler for getting results
  const getResults = useCallback((): ToolResultItem[] => {
    return pyramidItems.map((item, index) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: item.rank || index + 1,
      category: `${item.category}/${item.timeHorizon}`,
      classificationBreakdown: {
        pyramidCategory: item.category,
        timeHorizon: item.timeHorizon,
      },
      metadata: {
        category: item.category,
        timeHorizon: item.timeHorizon,
      },
    }));
  }, [pyramidItems, sourceIdMap]);

  // Register handlers with outer ToolWrapper so import and save work correctly
  useEffect(() => {
    onImportItems?.(handleImportItems as unknown as (items: ToolItem[]) => void);
  }, [handleImportItems, onImportItems]);

  const getLiveResults = useCallback((): ToolResultItem[] => {
    return liveRanking.map((id, idx) => {
      const item = liveRankItems.find(i => i.id === id);
      return {
        id: sourceIdMap[id] || id,
        name: item?.name || id,
        rank: idx + 1,
        score: liveRankItems.length - idx,
      };
    });
  }, [liveRanking, liveRankItems, sourceIdMap]);

  const liveRankingReady =
    isLiveSession && liveRankItems.length > 0 && liveRanking.length === liveRankItems.length;

  useEffect(() => {
    onGetResults?.(liveRankingReady ? getLiveResults : getResults);
  }, [getResults, getLiveResults, onGetResults, liveRankingReady]);
  
  // Create new pyramid and start building
  const handleStart = async () => {
    const id = await createPyramid({
      name: 'My Pyramid',
      createdBy: userId,
    });
    setActivePyramid(id);
    setState('building');
  };
  
  // Add a new item with specified cell (timeHorizon + category)
  const handleAddItem = async (name: string, description?: string, category?: Category, timeHorizon?: TimeHorizon) => {
    if (!activePyramidId) return;
    
    await addItem(activePyramidId, {
      name,
      description,
      timeHorizon: timeHorizon || 'Run',
      category: category || 'Other Priorities',
      createdBy: userId,
      source: 'manual',
    });
  };
  
  // Update categories (swim lanes)
  const handleUpdateCategories = async (newCategories: Category[]) => {
    if (!activePyramidId) return;
    await updateCategories(activePyramidId, newCategories);
  };
  
  // Update an item - only pass defined properties to avoid overwriting with undefined
  const handleUpdateItem = async (itemId: string, updates: Partial<LocalPyramidItem>) => {
    const cleanUpdates: Partial<StorePyramidItem> = {};
    if (updates.name !== undefined) cleanUpdates.name = updates.name;
    if (updates.description !== undefined) cleanUpdates.description = updates.description;
    if (updates.timeHorizon !== undefined) cleanUpdates.timeHorizon = updates.timeHorizon;
    if (updates.category !== undefined) cleanUpdates.category = updates.category;
    if (updates.rank !== undefined) cleanUpdates.rank = updates.rank;
    
    await updateItem(itemId, cleanUpdates);
  };
  
  // Delete an item
  const handleDeleteItem = async (itemId: string) => {
    await deleteItem(itemId);
  };
  
  // Move item to different segment
  const handleMoveItem = async (
    itemId: string,
    category: Category,
    timeHorizon: TimeHorizon,
    _strategicDirection: StrategicDirection
  ) => {
    await updateItem(itemId, { category, timeHorizon });
  };
  
  // Update stack rank within a segment
  const handleUpdateRank = async (itemId: string, newRank: number) => {
    await updateItemRank(itemId, newRank);
  };
  
  // Export handlers
  const handleExportExcel = useCallback(() => {
    if (!activePyramid) return;
    const itemsToExport = pyramidItems.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      timeHorizon: item.timeHorizon,
      rank: item.rank,
    }));
    exportPyramidToExcel(itemsToExport, categories, activePyramid.name);
    setShowExportMenu(false);
  }, [pyramidItems, categories, activePyramid]);
  
  const handleExportJSON = useCallback(() => {
    if (!activePyramid) return;
    const itemsToExport = pyramidItems.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      timeHorizon: item.timeHorizon,
      rank: item.rank,
    }));
    exportPyramidToJSON(itemsToExport, categories, activePyramid.name);
    setShowExportMenu(false);
  }, [pyramidItems, categories, activePyramid]);
  
  // Open Miro export modal
  const handleOpenMiroExport = useCallback(async () => {
    setShowExportMenu(false);
    setMiroExportError(null);
    setMiroExportSuccess(null);
    setMiroExportLoading(true);
    setShowMiroExportModal(true);
    
    // Get Miro token from localStorage (set by Connectors page)
    const miroToken = localStorage.getItem('peak_miro_token');
    if (!miroToken) {
      setMiroExportError('Please connect to Miro first via Settings > Connectors');
      setMiroExportLoading(false);
      return;
    }
    
    try {
      const api = getIntegrationsApi();
      // Connect and fetch boards
      await api.connectMiro(miroToken);
      const boards = await api.listMiroBoards();
      setMiroBoards(boards);
      setMiroExportLoading(false);
    } catch (err) {
      setMiroExportError(err instanceof Error ? err.message : 'Failed to connect to Miro');
      setMiroExportLoading(false);
    }
  }, []);
  
  // Export to Miro
  const handleExportToMiro = useCallback(async () => {
    console.log('[MaxPrioritiesPyramid] handleExportToMiro called');
    console.log('[MaxPrioritiesPyramid] activePyramid:', activePyramid);
    console.log('[MaxPrioritiesPyramid] pyramidItems.length:', pyramidItems.length);
    console.log('[MaxPrioritiesPyramid] pyramidItems:', pyramidItems);
    
    if (!activePyramid || pyramidItems.length === 0) {
      console.error('[MaxPrioritiesPyramid] Cannot export: no pyramid or no items');
      setMiroExportError(`Cannot export: ${!activePyramid ? 'No active pyramid' : 'No items to export'}`);
      return;
    }
    
    setMiroExportLoading(true);
    setMiroExportError(null);
    
    // Map time horizons to template format
    const mapTimeHorizonForExport = (th: string): string => {
      switch (th) {
        case 'Transform': return 'create_the_future';
        case 'Grow': return 'reinforce_foundation';
        case 'Run': return 'get_house_in_order';
        default: return 'get_house_in_order';
      }
    };
    
    try {
      const api = getIntegrationsApi();
      
      // Build a column index map from our categories
      const categoryToColIndex = new Map<string, number>();
      categories.forEach((cat, index) => {
        categoryToColIndex.set(cat, index);
      });
      
      const prioritiesForExport = pyramidItems.map(item => ({
        id: item.id,
        title: item.name,
        description: item.description || '',
        metadata: {
          timeHorizon: mapTimeHorizonForExport(item.timeHorizon),
          strategicTheme: item.category,
          // Add column index for positioning (since themes are custom, not template defaults)
          colIndex: categoryToColIndex.get(item.category) ?? 0,
          rank: item.rank,
        },
      }));
      
      console.log('[MaxPrioritiesPyramid] prioritiesForExport:', prioritiesForExport);
      console.log('[MaxPrioritiesPyramid] categories for frame labels:', categories);
      
      const result = await api.exportToMiroTemplate(
        'max-priorities-book-pyramid',
        prioritiesForExport,
        {
          boardId: selectedMiroBoard || undefined,
          createBoard: !selectedMiroBoard,
          newBoardName: newMiroBoardName || `${activePyramid.name} - ${new Date().toLocaleDateString()}`,
          clearExisting: false,
          duplicateHandling,
          // Pass the actual category names for frame creation
          customCategories: categories,
        }
      );
      
      if (result.success) {
        setMiroExportSuccess({
          boardUrl: result.boardUrl,
          itemsCreated: result.itemsCreated,
          itemsUpdated: result.itemsUpdated,
          itemsSkipped: result.itemsSkipped,
          duplicatesFound: result.duplicatesFound,
        });
        // Open the board in a new tab if URL is available
        if (result.boardUrl) {
          window.open(result.boardUrl, '_blank');
        }
      } else {
        setMiroExportError('Export failed. Please try again.');
      }
    } catch (err) {
      setMiroExportError(err instanceof Error ? err.message : 'Failed to export to Miro');
    } finally {
      setMiroExportLoading(false);
    }
  }, [activePyramid, pyramidItems, categories, selectedMiroBoard, newMiroBoardName, duplicateHandling]);
  
  // Delete current pyramid
  const handleDeletePyramid = useCallback(async () => {
    if (!activePyramidId) return;
    if (confirm('This will permanently delete this pyramid and all its items. Are you sure?')) {
      await deletePyramid(activePyramidId);
      setState('setup');
    }
  }, [activePyramidId, deletePyramid]);
  
  // Handle imported items from PyramidImportModal
  // The modal passes PyramidItem[] - we create candidates and link them to pyramid items
  const handlePyramidImport = async (
    pyramidItems: Array<{
      id: string;
      title: string;
      description?: string;
      timeHorizon: string;
      strategicThemeId?: string;
      strategicThemeName?: string;
      rankInCell?: number;
      position?: { row: number; col: number };
      source?: string;
      externalId?: string;
      metadata?: Record<string, unknown>;
    }>,
    duplicateHandlingOption: 'skip' | 'update' | 'create' = 'skip'
  ) => {
    console.log('[MaxPrioritiesPyramid] handlePyramidImport called with', pyramidItems.length, 'items');
    console.log('[MaxPrioritiesPyramid] Duplicate handling:', duplicateHandlingOption);
    console.log('[MaxPrioritiesPyramid] Items received:', pyramidItems.slice(0, 3).map(i => ({
      id: i.id,
      title: i.title?.substring(0, 30),
      timeHorizon: i.timeHorizon,
      strategicThemeName: i.strategicThemeName
    })));
    
    // Map time horizon values from import format to our format
    const mapTimeHorizon = (th: string): TimeHorizon => {
      const lower = th.toLowerCase().replace(/_/g, ' ');
      if (lower.includes('future') || lower.includes('transform')) return 'Transform';
      if (lower.includes('foundation') || lower.includes('grow') || lower.includes('reinforce')) return 'Grow';
      return 'Run'; // Default to "Run" (Get House in Order)
    };
    
    // Normalize title for comparison
    const normalizeTitle = (title: string): string => {
      return title.toLowerCase().trim().replace(/\s+/g, ' ');
    };
    
    let targetPyramidId = activePyramidId;
    
    if (!targetPyramidId) {
      // Create a new pyramid first
      console.log('[MaxPrioritiesPyramid] Creating new pyramid...');
      const id = await createPyramid({
        name: 'Imported Pyramid',
        createdBy: userId,
      });
      targetPyramidId = id;
      setActivePyramid(id);
    }
    
    // Get existing items for duplicate detection
    const existingItems = usePyramidStore.getState().items.filter(i => i.pyramidId === targetPyramidId);
    const existingTitlesMap = new Map<string, typeof existingItems[0]>();
    existingItems.forEach(item => {
      existingTitlesMap.set(normalizeTitle(item.name), item);
    });
    console.log('[MaxPrioritiesPyramid] Existing items in pyramid:', existingItems.length);
    
    // Track import stats
    let itemsCreated = 0;
    let itemsUpdated = 0;
    let itemsSkipped = 0;
    
    // Filter and process items based on duplicate handling
    const itemsToImport: Array<{
      name: string;
      description?: string;
      timeHorizon: TimeHorizon;
      category: string;
      rank: number;
      source: 'miro' | 'imported';
      linkedCandidateId: string;
      externalId?: string;
      metadata?: Record<string, unknown>;
    }> = [];
    
    for (const item of pyramidItems) {
      const normalizedTitle = normalizeTitle(item.title);
      const existingItem = existingTitlesMap.get(normalizedTitle);
      
      if (existingItem) {
        console.log('[MaxPrioritiesPyramid] Duplicate found:', item.title);
        
        if (duplicateHandlingOption === 'skip') {
          itemsSkipped++;
          continue;
        } else if (duplicateHandlingOption === 'update') {
          // Update existing item with decoded category
          const decodedCat = item.strategicThemeName 
            ? decodeHtmlEntities(item.strategicThemeName) 
            : 'Other Priorities';
          await updateItem(existingItem.id, {
            description: item.description,
            timeHorizon: mapTimeHorizon(item.timeHorizon),
            category: decodedCat,
            rank: item.rankInCell || existingItem.rank,
          });
          itemsUpdated++;
          continue;
        }
        // 'create' falls through to create a new item anyway
      }
      
      // Create an item for each pyramid entry (lifecycle_stage: intake)
      const candidateId = generateUUID();
      
      // Add the item to candidates store with all available metadata
      createItem({
        id: candidateId,
        title: item.title,
        description: item.description || '',
        createdBy: userId,
        source: 'imported',
        // Store pyramid reference in item metadata
        metadata: {
          pyramidId: targetPyramidId,
          timeHorizon: mapTimeHorizon(item.timeHorizon),
          strategicTheme: item.strategicThemeName || 'Other Priorities',
          externalId: item.externalId,
          originalMiroId: item.id,
          importedAt: new Date().toISOString(),
          // Store user name for display in queues
          createdByName: userName || 'Anonymous',
          submitterName: userName || 'Anonymous',
          ...(item.metadata || {}),
        },
      });
      
      console.log('[MaxPrioritiesPyramid] Created candidate', candidateId, 'for', item.title);
      itemsCreated++;
      
      // Decode HTML entities in theme name
      const decodedCategory = item.strategicThemeName 
        ? decodeHtmlEntities(item.strategicThemeName) 
        : 'Other Priorities';
      
      itemsToImport.push({
        name: item.title,
        description: item.description,
        timeHorizon: mapTimeHorizon(item.timeHorizon),
        category: decodedCategory,
        rank: item.rankInCell || 1,
        source: (item.source === 'miro' ? 'miro' : 'imported') as 'miro' | 'imported',
        linkedCandidateId: candidateId,
        externalId: item.externalId || item.id,
        metadata: {
          originalMiroId: item.id,
          position: item.position,
          importedAt: new Date().toISOString(),
          ...(item.metadata || {}),
        },
      });
    }
    
    console.log('[MaxPrioritiesPyramid] Import stats - Created:', itemsCreated, 'Updated:', itemsUpdated, 'Skipped:', itemsSkipped);
    console.log('[MaxPrioritiesPyramid] Importing', itemsToImport.length, 'new items to pyramid', targetPyramidId);
    
    // Import items to pyramid (with linked candidate IDs)
    if (itemsToImport.length > 0) {
      await importItems(targetPyramidId, itemsToImport, userId);
    }
    
    // Update categories - REPLACE with imported themes (don't keep old defaults)
    const newThemes: string[] = [];
    pyramidItems.forEach(item => {
      if (item.strategicThemeName) {
        // Decode HTML entities in theme names
        const decodedTheme = decodeHtmlEntities(item.strategicThemeName);
        if (!newThemes.includes(decodedTheme)) {
          newThemes.push(decodedTheme);
        }
      }
    });
    
    // If we have imported themes, use them instead of defaults
    if (newThemes.length > 0) {
      console.log('[MaxPrioritiesPyramid] Replacing categories with imported themes:', newThemes);
      await updateCategories(targetPyramidId, newThemes);
    }
    
    console.log('[MaxPrioritiesPyramid] Import complete.');
    
    // Debug: Log store state after import
    const storeState = usePyramidStore.getState();
    console.log('[MaxPrioritiesPyramid] - Pyramids in store:', storeState.pyramids.map(p => ({ id: p.id, name: p.name })));
    console.log('[MaxPrioritiesPyramid] - Active pyramid ID:', storeState.activePyramidId);
    console.log('[MaxPrioritiesPyramid] - Active pyramid found:', !!storeState.pyramids.find(p => p.id === storeState.activePyramidId));
    console.log('[MaxPrioritiesPyramid] - Pyramid items:', storeState.items.length);
    console.log('[MaxPrioritiesPyramid] - Items:', useItemsStore.getState().items.length);
    
    // Verify the pyramid exists in the store - if not, refetch from Supabase
    const pyramidInStore = storeState.pyramids.find(p => p.id === targetPyramidId);
    if (!pyramidInStore) {
      console.warn('[MaxPrioritiesPyramid] Pyramid not found in store after import, refetching from Supabase...');
      await fetchFromSupabase();
      // Re-set the active pyramid after fetch
      setActivePyramid(targetPyramidId);
    }
    
    // Close modal and transition to building state
    setShowImportModal(false);
    setState('building');
    
    // Return import stats for display
    return { itemsCreated, itemsUpdated, itemsSkipped };
  };
  
  // Group items by segment (for PyramidView)
  const segments = useMemo(() => {
    const segmentMap = new Map<string, LocalPyramidItem[]>();
    
    localItems.forEach((item) => {
      const key = `${item.category}-${item.timeHorizon}-${item.strategicDirection}`;
      if (!segmentMap.has(key)) {
        segmentMap.set(key, []);
      }
      segmentMap.get(key)!.push(item);
    });
    
    segmentMap.forEach((segmentItems) => {
      segmentItems.sort((a, b) => a.rank - b.rank);
    });
    
    return segmentMap;
  }, [localItems]);
  
  // Supabase status indicator
  const supabaseAvailable = isSupabaseAvailable();
  
  return (
    <div className="w-full">
      {/* Supabase Status Banner */}
      {!supabaseAvailable && !isLiveSession && (
        <div className="mb-4 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] flex items-center gap-2 text-[var(--mustard)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Supabase not configured. Data will not be persisted. Configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to enable persistence.</span>
        </div>
      )}
      
      {/* Loading State */}
      {syncState.isLoading && !isLiveSession && (
        <div className="mb-4 p-3 bg-[var(--slate-dim)] border border-[var(--slate)] flex items-center gap-2 text-[var(--text-sec)] text-sm">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
          <span>Loading pyramids from database...</span>
        </div>
      )}
      
      {/* Toolbar - shown when building/viewing */}
      {(state === 'building' || state === 'viewing') && activePyramid && !isLiveSession && (
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4 bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          {/* Left: Pyramid name and sync status */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                value={activePyramid.name}
                onChange={(e) => updatePyramid(activePyramidId!, { name: e.target.value })}
                className="text-lg font-semibold border-b border-transparent hover:border-[var(--rule)] focus:border-[var(--slate)] focus:outline-none px-1 py-0.5"
                placeholder="Pyramid name..."
              />
              {pyramids.length > 1 && (
                <button
                  onClick={() => setShowPyramidSelector(!showPyramidSelector)}
                  className="ml-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  ({pyramids.length} pyramids)
                </button>
              )}
              
              {/* Pyramid Selector Dropdown */}
              {showPyramidSelector && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowPyramidSelector(false)} />
                  <div className="absolute left-0 mt-1 w-64 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg z-20 py-1">
                    {pyramids.map(p => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setActivePyramid(p.id);
                          setShowPyramidSelector(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-[var(--surface)] flex items-center justify-between ${
                          p.id === activePyramidId ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'text-[var(--text-sec)]'
                        }`}
                      >
                        <span className="truncate">{p.name}</span>
                        <span className="text-xs text-[var(--text-ter)]">
                          {getItemsForPyramid(p.id).length} items
                        </span>
                      </button>
                    ))}
                    <div className="border-t border-[var(--rule)] mt-1 pt-1">
                      <button
                        onClick={async () => {
                          const id = await createPyramid({ name: 'New Pyramid', createdBy: userId });
                          setActivePyramid(id);
                          setShowPyramidSelector(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Create New Pyramid
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Sync status */}
            <div className="text-xs text-[var(--text-ter)] flex items-center gap-1">
              {syncState.isSyncing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Syncing...</span>
                </>
              ) : syncState.lastSyncAt ? (
                <>
                  <Database className="h-3 w-3 text-[var(--green)]" />
                  <span>Synced</span>
                </>
              ) : supabaseAvailable ? (
                <span className="text-[var(--mustard)]">Not synced</span>
              ) : null}
            </div>
          </div>
          
          {/* Right: Action buttons */}
          <div className="flex items-center gap-2">
            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="px-3 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] flex items-center gap-2 text-sm font-medium hover:bg-[var(--hover-tint)]"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg z-20 py-1">
                    <button
                      onClick={handleExportExcel}
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-[var(--green)]" />
                      Export as Excel
                    </button>
                    <button
                      onClick={handleExportJSON}
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] flex items-center gap-2"
                    >
                      <FileJson className="h-4 w-4 text-[var(--text-ter)]" />
                      Export as JSON
                    </button>
                    <div className="border-t border-[var(--rule)] my-1" />
                    <button
                      onClick={handleOpenMiroExport}
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] flex items-center gap-2"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#FFD02F">
                        <path d="M17.392 0H13.9L17 4.808 10.444 0H6.949l3.102 5.696L3.5 0H0l6.949 12.576L0 24h3.5l6.551-11.196L6.949 24h3.495L17 12.866 13.9 24h3.492L24 12.042 17.392 0z" />
                      </svg>
                      Export to Miro
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Import Button */}
            {mayImport && (
              <button
                onClick={() => setShowImportModal(true)}
                className="px-3 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] flex items-center gap-2 text-sm font-medium hover:bg-[var(--hover-tint)]"
              >
                <Upload className="h-4 w-4" />
                Import
              </button>
            )}
            
            {/* Delete Button */}
            <button
              onClick={handleDeletePyramid}
              className="px-3 py-2 bg-[var(--coral-dim)] text-[var(--coral)] flex items-center gap-2 text-sm font-medium hover:bg-[var(--coral-dim)]"
              title="Delete this pyramid"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {isLiveSession && liveRankItems.length > 0 && (
        <LiveToolScaffold
          title="Max priorities"
          subtitle="Rank session items by priority (#1 = most important)"
          progressDone={liveRanking.length}
          progressTotal={liveRankItems.length}
        >
          <StackRankingLive
            items={liveRankItems}
            ranking={liveRanking}
            onRankingChange={setLiveRanking}
          />
        </LiveToolScaffold>
      )}

      {/* Main Content */}
      {!isLiveSession && (
      <>
      <div>
        {state === 'setup' && (
          <>
            {isAppMode && (
              <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                <p className="text-sm text-[var(--text-sec)]">
                  {`Import items from your ${importSourceLabel} using the header button, start an empty pyramid, or bring in a pyramid from Miro or Excel.`}
                </p>
              </div>
            )}
            <ItemInput onStart={handleStart} />
            <div className="mt-6 text-center space-y-3">
              {mayImport && (
                <button
                  onClick={() => setShowImportModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-consulting-slate-blue-700 bg-consulting-slate-blue-50 hover:bg-consulting-slate-blue-100 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Import from Miro or Excel
                </button>
              )}
              
              {pyramids.length > 0 && (
                <div className="pt-4 border-t border-[var(--rule)] mt-4">
                  <p className="text-sm text-[var(--text-ter)] mb-2">Or resume an existing pyramid:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {pyramids.slice(0, 5).map(p => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setActivePyramid(p.id);
                          setState('building');
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                      >
                        <Database className="h-4 w-4" />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {(state === 'building' || state === 'viewing') && (
          <PyramidView
            items={localItems}
            segments={segments}
            categories={categories}
            onAddItem={handleAddItem}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
            onMoveItem={handleMoveItem}
            onUpdateRank={handleUpdateRank}
            onViewMode={() => {}}
            onEditMode={() => {}}
            isViewMode={false}
            onUpdateCategories={handleUpdateCategories}
          />
        )}
      </div>
      
      {/* Import Modal */}
      {showImportModal && mayImport && (
        <PyramidImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handlePyramidImport}
          categories={categories}
          existingItemCount={pyramidItems.length}
        />
      )}
      
      {/* Miro Export Modal */}
      {showMiroExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2" style={{ backgroundColor: '#FFD02F' }}>
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="#050038">
                    <path d="M17.392 0H13.9L17 4.808 10.444 0H6.949l3.102 5.696L3.5 0H0l6.949 12.576L0 24h3.5l6.551-11.196L6.949 24h3.495L17 12.866 13.9 24h3.492L24 12.042 17.392 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Export to Miro</h2>
                  <p className="text-sm text-[var(--text-ter)]">Create sticky notes on a Miro board</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowMiroExportModal(false);
                  setMiroExportSuccess(null);
                  setMiroExportError(null);
                }} 
                className="p-2 hover:bg-[var(--hover-tint)]"
              >
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {miroExportLoading && !miroExportSuccess && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-[var(--mustard)]" />
                  <span className="ml-3 text-[var(--text-sec)]">Loading...</span>
                </div>
              )}
              
              {miroExportError && (
                <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)] mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[var(--coral)]">Error</p>
                      <p className="text-sm text-[var(--coral)]">{miroExportError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {miroExportSuccess && (
                <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Export Complete!</p>
                      <div className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] space-y-1">
                        {miroExportSuccess.itemsCreated > 0 && (
                          <p>Created {miroExportSuccess.itemsCreated} new sticky notes.</p>
                        )}
                        {(miroExportSuccess.itemsUpdated ?? 0) > 0 && (
                          <p>Updated {miroExportSuccess.itemsUpdated} existing sticky notes.</p>
                        )}
                        {(miroExportSuccess.itemsSkipped ?? 0) > 0 && (
                          <p>Skipped {miroExportSuccess.itemsSkipped} duplicates (already in Miro).</p>
                        )}
                        {miroExportSuccess.itemsCreated === 0 && 
                         (miroExportSuccess.itemsUpdated ?? 0) === 0 && 
                         (miroExportSuccess.itemsSkipped ?? 0) > 0 && (
                          <p className="text-[var(--mustard)]">All items already exist in the board.</p>
                        )}
                      </div>
                      {miroExportSuccess.boardUrl && (
                        <a
                          href={miroExportSuccess.boardUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] underline"
                        >
                          Open Board <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {!miroExportLoading && !miroExportSuccess && !miroExportError && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Destination Board
                    </label>
                    <select
                      value={selectedMiroBoard}
                      onChange={(e) => setSelectedMiroBoard(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500 focus:border-[var(--mustard)]"
                    >
                      <option value="">Create new board</option>
                      {miroBoards.map(board => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {!selectedMiroBoard && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                        New Board Name
                      </label>
                      <input
                        type="text"
                        value={newMiroBoardName}
                        onChange={(e) => setNewMiroBoardName(e.target.value)}
                        placeholder={activePyramid?.name || 'My Pyramid'}
                        className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500 focus:border-[var(--mustard)]"
                      />
                    </div>
                  )}
                  
                  {selectedMiroBoard && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                        If duplicates are found
                      </label>
                      <select
                        value={duplicateHandling}
                        onChange={(e) => setDuplicateHandling(e.target.value as 'skip' | 'update' | 'create')}
                        className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500 focus:border-[var(--mustard)]"
                      >
                        <option value="skip">Skip duplicates (don't create if title already exists)</option>
                        <option value="update">Update existing (move to correct position)</option>
                        <option value="create">Create anyway (may result in duplicates)</option>
                      </select>
                      <p className="mt-1 text-xs text-[var(--text-ter)]">
                        Duplicates are detected by matching item titles.
                      </p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-[var(--surface)]">
                    <p className="text-sm text-[var(--text-sec)]">
                      <strong>{pyramidItems.length}</strong> items will be exported as sticky notes, 
                      positioned according to their time horizon and strategic theme.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowMiroExportModal(false);
                  setMiroExportSuccess(null);
                  setMiroExportError(null);
                }}
                className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                {miroExportSuccess ? 'Close' : 'Cancel'}
              </button>
              {!miroExportSuccess && (
                <button
                  onClick={handleExportToMiro}
                  disabled={miroExportLoading || pyramidItems.length === 0}
                  className="px-4 py-2 text-[var(--text-pri)] font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#FFD02F' }}
                >
                  {miroExportLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      Export to Miro
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

// Export with ToolWrapper for shared product integration
export default function MaxPrioritiesPyramid(props: ToolHostProps) {
  // Use refs so that the stable wrappers are always defined on first render,
  // allowing ToolWrapper's zero-state interstitial to display correctly.
  const importHandlerRef = useRef<((items: ToolItem[]) => void) | null>(null);
  const resultsHandlerRef = useRef<(() => ToolResultItem[]) | null>(null);
  const { items: pyramidItems } = usePyramidStore();

  const stableImportHandler = useCallback((items: ToolItem[]) => {
    importHandlerRef.current?.(items);
  }, []);

  const stableResultsHandler = useCallback((): ToolResultItem[] => {
    return resultsHandlerRef.current?.() ?? [];
  }, []);

  return (
    <ToolWrapper
      toolName="Max Priorities Pyramid"
      toolDescription="Visualize relationships among priorities across time and categories. Connect high-level goals to tactical actions."
      onImportItems={stableImportHandler}
      onGetResults={stableResultsHandler}
      itemCount={pyramidItems.length}
      {...props}
    >
      <MaxPrioritiesPyramidContent 
        onImportItems={(handler) => { importHandlerRef.current = handler; }}
        onGetResults={(handler) => { resultsHandlerRef.current = handler; }}
      />
    </ToolWrapper>
  );
}
